import React from 'react';
import { colors, font } from '../../styles/tokens';

export type PanelTone = 'default' | 'ok' | 'warn' | 'alert';

const TONE_COLOR: Record<PanelTone, string> = {
  default: colors.chrome.base,
  ok: colors.accent.green,
  warn: colors.accent.yellow,
  alert: colors.accent.red,
};

interface PanelProps {
  title: string;
  /** Optional right-hand chip: a count, a status word, a date. */
  badge?: React.ReactNode;
  /** Colours the badge. `alert` also tints the panel's own border. */
  badgeTone?: PanelTone;
  children: React.ReactNode;
  style?: React.CSSProperties;
  /** Drop the body padding when the child manages its own (a full-bleed table). */
  flush?: boolean;
}

/**
 * A workspace panel, in dse-terminal's idiom.
 *
 * The point of this component is SEPARATION. A section label floating at the top of a card
 * body does not tell you where one section ends and the next begins — you have to read the
 * text to find the boundary. A panel with a distinct header bar does it with structure:
 *
 *   - a header strip on its own ground, divided from the body by a rule
 *   - an amber chrome tick with a soft glow, which the eye finds before the words
 *   - an uppercase, wide-tracked heading, so headings never read as content
 *   - an optional badge carrying the one fact that qualifies the section
 *
 * Together those give a page of stacked panels a rhythm you can scan without reading, which
 * is the whole difference between a dashboard and a wall of cards.
 */
export function Panel({ title, badge, badgeTone = 'default', children, style, flush }: PanelProps) {
  const tone = TONE_COLOR[badgeTone];
  return (
    <div
      style={{
        backgroundColor: colors.bg.secondary,
        border: `1px solid ${badgeTone === 'alert' ? `${colors.accent.red}66` : colors.border.subtle}`,
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minWidth: 0,
        ...style,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '6px 10px 5px',
          borderBottom: `1px solid ${colors.border.subtle}`,
          // The sheen washes down from the top edge, so the header reads as a lit strip
          // rather than a differently-coloured block.
          background: `linear-gradient(180deg, ${colors.chrome.base}0d, transparent)`,
          flexShrink: 0,
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
          <span
            style={{
              width: 5,
              height: 11,
              borderRadius: 1,
              background: colors.chrome.base,
              boxShadow: `0 0 6px ${colors.chrome.base}`,
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: colors.chrome.base,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </span>
        </span>

        {badge != null && (
          <span
            style={{
              fontFamily: font.family.mono,
              fontSize: 9.5,
              fontWeight: 600,
              color: tone,
              background: `${tone}1f`,
              border: `1px solid ${tone}4d`,
              padding: '1px 7px',
              borderRadius: 2,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {badge}
          </span>
        )}
      </div>

      <div style={{ padding: flush ? 0 : 12, minWidth: 0, flex: 1 }}>{children}</div>
    </div>
  );
}
